<template>
  <LoadingModal v-if="loading"/>

  <div class="pageContent" v-else>
    <div class="detailBox">
      <div class="filters">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <div class="control">
                <div class="select is-small">
                  <select v-model="filters.user">
                    <option value selected>filtrovat dle autora</option>
                    <option
                      v-for="user in users"
                      :key="user.username"
                      :value="user.username"
                    >{{ user.name }}</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="level-item">
              <div class="control">
                <div class="select is-small">
                  <select v-model="filters.field">
                    <option value selected>filtrovat dle předmětu</option>
                    <option
                      v-for="field in fields"
                      :key="field.shorthand"
                      :value="field.shorthand"
                    >{{ field.title }}</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="level-item">
              <div class="control">
                <div class="select is-small">
                  <select v-model="filters.uploaded">
                    <option value selected>filtrovat dle stavu práce</option>
                    <option value="true">nahrána</option>
                    <option value="false">nenahrána</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="theses.length">
        <div class="detailSection" v-for="thesis in theses" :key="thesis.origin.username">
          <h2 class="is-2 is-title" v-if="thesis.title">{{ thesis.title }}</h2>
          <h2 class="is-2 is-title is-blank" v-else>Maturitní práce bez názvu</h2>
          <h6 class="is-6 is-subtitle">{{ thesis.origin.fullName }} — {{ thesis.field.title }}</h6>
          <div class="level">
            <div class="level-left">
              <div class="field is-grouped">
                <div class="control">
                  <div class="tags has-addons">
                    <span class="tag is-dark">vedoucí práce</span>
                    <span class="tag is-light">{{ thesis.supervisor.fullName }}</span>
                  </div>
                </div>

                <div class="control">
                  <div class="tags has-addons">
                    <span class="tag is-dark">oponent práce</span>
                    <span class="tag is-light">{{ thesis.reader.fullName }}</span>
                  </div>
                </div>

                <div class="control">
                  <div class="tags has-addons">
                    <span class="tag is-dark">stav práce</span>
                    <span class="tag is-success" v-if="thesis.uploaded">nahrána</span>
                    <span class="tag is-warning" v-else>nenahrána</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="level-right">
              <div class="level-item">
                <button
                  class="btn spaceless isTurquoise"
                  @click="goToReport('supervisor', thesis.uuid)"
                  v-if="thesis.report.supervisor"
                >
                  <icon type="eye" size="16"/>Posudek vedoucího
                </button>
                <button class="btn spaceless isTurquoise isTranslucent" v-else>
                  <icon type="eye" size="16"/>Posudek vedoucího
                </button>

                <button
                  class="btn spaceless isTurquoise"
                  @click="goToReport('reader', thesis.uuid)"
                  v-if="thesis.report.reader"
                >
                  <icon type="eye" size="16"/>Posudek oponenta
                </button>
                <button class="btn spaceless isTurquoise isTranslucent" v-else>
                  <icon type="eye" size="16"/>Posudek oponenta
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <em v-else>Nastaveným filtrům neodpovídají žádné maturitní práce.</em>
    </div>
  </div>
</template>

<script>
import { fetchThesesAll, fetchThesisReports } from '../http';
import LoadingModal from '../components/LoadingModal';

export default {
  data() {
    return {
      loading: true,
      data: null,
      filters: {
        user: '',
        field: '',
        uploaded: ''
      }
    };
  },

  methods: {
    goToReport(author, uuid) {
      const thesis = this.data.filter(thesis => thesis.uuid === uuid)[0];

      const metadata = {
        thesisAuthor: thesis.origin.fullName,
        author: thesis[author].fullName,
        className: thesis.origin.class,
        field: thesis.field.title || null,
        title: thesis.title || null
      };

      this.$store.commit('saveReport', {
        report: thesis.report[author],
        metadata,
        author
      });

      this.$router.push({ path: 'report', query: { author } });
    }
  },

  computed: {
    users() {
      return this.data
        .map(thesis => thesis.origin)
        .map(origin => ({ name: origin.fullName.split(' '), username: origin.username }))
        .sort((a, b) => a.name[1].localeCompare(b.name[1]))
        .map(origin => ({ name: origin.name.join(' '), username: origin.username }));
    },

    fields() {
      return this.data.map(thesis => thesis.field);
    },

    theses() {
      let theses = this.data;

      if (this.filters.user) {
        theses = theses.filter(thesis => thesis.origin.username === this.filters.user);
      }

      if (this.filters.field) {
        theses = theses.filter(thesis => thesis.field.shorthand === this.filters.field);
      }

      if (this.filters.uploaded) {
        if (this.filters.uploaded === 'true') {
          theses = theses.filter(thesis => thesis.uploaded);
        } else {
          theses = theses.filter(thesis => !thesis.uploaded);
        }
      }

      return theses;
    }
  },

  components: {
    LoadingModal
  },

  async created() {
    try {
      const username = this.$store.getters.username;

      const {
        data: { data }
      } = await fetchThesesAll();

      this.data = data;

      for (let thesis of this.data) {
        const {
          data: { data }
        } = await fetchThesisReports(thesis.uuid);

        const toPush = {};

        for (let role of ['supervisor', 'reader']) {
          if (data[role]) {
            const sourceReport = data[role];

            const report = {
              points: [
                sourceReport.assessment_1,
                sourceReport.assessment_2,
                sourceReport.assessment_3,
                sourceReport.assessment_4,
                sourceReport.assessment_5,
                sourceReport.assessment_6,
                sourceReport.assessment_7
              ],
              text: sourceReport.assessment_text,
              questions: sourceReport.questions
            };

            toPush[role] = report;
          } else {
            toPush[role] = null;
          }
        }

        thesis.report = toPush;
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.loading = false;
    }
  }
};
</script>

<style lang="sass" scoped>
@import '../styles/buttons.sass'

h2.is-blank
  opacity: .25

h2.is-title
  margin-bottom: .5rem

h6
  color: gray
  font-weight: normal

.detailBox
  background: white
  box-shadow: 0 2px 3px rgba(10,10,10, .1), 0 0 0 1px rgba(10,10,10, .1)
  padding: 3.25rem 2rem
  margin: 1.75rem 0

.detailSection
  padding: 1.5rem 0

  &:not(:first-child)
    border-top: 1px solid #f5f5f5

.filters
  padding: 0 0 1.5rem

  & .select:not(.is-multiple):not(.is-loading)::after
    border-color: #5182EC

.btn.isTranslucent
  cursor: not-allowed
</style>
